import { GatewayExportDocuments } from "@conformite/gateway";
import {
  useDialog,
  Button,
  displayErrorToast,
  displayInfoToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { GatewayExportDocumentsApi } from "@src/api/export-document.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { downloadFile } from "@src/helper/file.helper";
import { useEffect, useState } from "react";

const getExportDocumentURL = async (
  type: GatewayExportDocuments.ExportDocumentType
): Promise<string | null> => {
  try {
    const response = await GatewayExportDocumentsApi.getSignedURL(type);
    if (!response) {
      throw new Error("Failed to fetch signed URL");
    }
    return response.data.url;
  } catch (error) {
    console.error("Error fetching signed URL:", error);
    return null;
  }
};

export function ExportFilesButton({
  type,
}: {
  type: GatewayExportDocuments.ExportDocumentType;
}) {
  const [isDataLoading, setDataIsLoading] = useState(false);
  const [queryData, setQueryData] = useState<string>();
  const [failureCount, setFailureCount] = useState(0);
  const [alreadyClicked, setAlreadyClicked] = useState(false);
  const maximumRetry = 6;
  const retryInterval = 30_000 / maximumRetry;

  useEffect(() => {
    if (failureCount <= maximumRetry) {
      return;
    }
    displayErrorToast(
      type === GatewayExportDocuments.ExportDocumentType.ADHESION
        ? "Le traitement est plus long que prévu, nous vous enverrons un mail dès que les attestations seront prêtes."
        : "Le traitement est plus long que prévu, nous vous enverrons un mail dès que les documents seront prêts."
    );
    setDataIsLoading(false);
    setAlreadyClicked(false);
  }, [failureCount, maximumRetry]);

  useEffect(() => {
    if (!queryData) return;

    displaySucessToast("L'export est prêt, vous pouvez le télécharger");
    downloadFile(queryData);
    setDataIsLoading(false);
    setFailureCount(0);
    setAlreadyClicked(false);
  }, [queryData]);

  const { openConfirmDialog } = useDialog();
  const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = true; // Included for legacy support, e.g. Chrome/Edge < 119
  };

  const retryAttempt = async (attempt = 0) => {
    if (attempt > maximumRetry) {
      return;
    }

    const url = await getExportDocumentURL(type);
    if (url) {
      setQueryData(url);
      return;
    }

    setFailureCount(attempt + 1);

    setTimeout(() => {
      void retryAttempt(attempt + 1);
    }, retryInterval);
  };

  const handleClick = async () => {
    if (!alreadyClicked) {
      const shouldContinue = await openConfirmDialog({
        title:
          type === GatewayExportDocuments.ExportDocumentType.ADHESION
            ? "Vous êtes sur le point de télécharger les attestations d'adhésion pour l'ensemble des entreprises qui sont conformes à ce jour."
            : "Vous êtes sur le point de télécharger les attestations d'honorabilité pour l'ensemble des personnes assujetties.",
        confirmText: "Confirmer",
        cancelText: "Annuler",
      });
      if (!shouldContinue) return;
    }

    window.addEventListener("beforeunload", beforeUnloadHandler);

    displayInfoToast(
      type === GatewayExportDocuments.ExportDocumentType.ADHESION
        ? "Les attestations sont en cours de préparation. Merci de patienter."
        : "Les documents sont en cours de préparation. Merci de patienter.",
      {
        hideAfter: 10_000,
      }
    );

    setDataIsLoading(true);
    setAlreadyClicked(true);
    setFailureCount(0);

    try {
      await GatewayExportDocumentsApi.exportDocuments(type);
      void retryAttempt();
    } catch (error) {
      const displayGenericError = () =>
        displayErrorToast(
          "Une erreur est survenue, merci de ressayer plus tard ou de contacter le support.",
          {
            hideAfter: 10_000,
          }
        );
      handleGatewayError({
        onResponse: (gatewayError) => {
          if (gatewayError.response.status === 403)
            displayErrorToast(gatewayError.response.data.message, {
              hideAfter: 10_000,
            });
          else displayGenericError();
        },
        onUnhandled: displayGenericError,
      })(error);
      setDataIsLoading(false);
      setAlreadyClicked(false);
    } finally {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    }
  };

  return (
    <Button
      variant="text"
      onClick={() => {
        void handleClick();
      }}
      disabled={isDataLoading}
      icon={isDataLoading ? <Loader /> : undefined}
    >
      {type === GatewayExportDocuments.ExportDocumentType.ADHESION
        ? "Exporter les attestations d'adhésion disponibles"
        : "Exporter les attestations d'honorabilité"}
    </Button>
  );
}
